import { RowCrudStatus } from "../../common/types";
import {
  Country,
  CountryCreateMutationVariables,
  CountryUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { blankToNull } from "../../../common/blankToNull";

export type CountryValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  name_es: string;
  name_en: string;
  name_pt: string;
  code: string;
  currency: string;
  demonym_es: string;
  demonym_en: string;
  demonym_pt: string;
};

export type CountryErrorsType = {
  name_es?: { message: string; fieldLabel: string };
  name_en?: { message: string; fieldLabel: string };
  name_pt?: { message: string; fieldLabel: string };
  code?: { message: string; fieldLabel: string };
  currency?: { message: string; fieldLabel: string };
  demonym_es?: { message: string; fieldLabel: string };
  demonym_en?: { message: string; fieldLabel: string };
  demonym_pt?: { message: string; fieldLabel: string };
};

export function CountryValuesInit(): CountryValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    name_es: "",
    name_en: "",
    name_pt: "",
    code: "",
    currency: "",
    demonym_es: "",
    demonym_en: "",
    demonym_pt: "",
  };
}

export type CountryType = Country | CountryValuesType;

export function countryDisplayId(
  source: Partial<Country>,
  _langStore: LangStore
) {
  return `${source[_langStore.fieldByLang("name") as keyof CountryType]}`;
}

export function countryDisplayIdByDemonym(
  source: Partial<Country>,
  _langStore: LangStore
): string {
  const propio = String(
    source[_langStore.fieldByLang("demonym") as keyof CountryType] ?? ""
  ).trim();
  return propio ? propio : countryDisplayId(source, _langStore);
}

export function getCountryValues(
  source: CountryType,
  _langStore: LangStore,
  _duplicated: boolean = false
): CountryValuesType {
  const entityValues = CountryValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : countryDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.name_es = source.name_es;
  entityValues.name_en = source.name_en;
  entityValues.name_pt = source.name_pt;
  entityValues.code = source.code;
  entityValues.currency = source.currency;
  entityValues.demonym_es = source.demonym_es || "";
  entityValues.demonym_en = source.demonym_en || "";
  entityValues.demonym_pt = source.demonym_pt || "";
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getCountryUpdateVariables(
  values: CountryValuesType
): CountryUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    name_es: values.name_es,
    name_en: values.name_en,
    name_pt: values.name_pt,
    code: values.code,
    currency: values.currency,
    demonym_es: blankToNull(values.demonym_es),
    demonym_en: blankToNull(values.demonym_en),
    demonym_pt: blankToNull(values.demonym_pt),
  };
}

export function getCountryCreateVariables(
  values: CountryValuesType
): CountryCreateMutationVariables {
  return {
    name_es: values.name_es,
    name_en: values.name_en,
    name_pt: values.name_pt,
    code: values.code,
    currency: values.currency,
    demonym_es: blankToNull(values.demonym_es),
    demonym_en: blankToNull(values.demonym_en),
    demonym_pt: blankToNull(values.demonym_pt),
  };
}
